import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Phase 207c — Centralized Meeting Templates Dialog
 *
 * Reusable modal that edits the global "Πρότυπα Κειμένων Συνελεύσεων"
 * (Legal Quorum + Committee Decision). Templates apply to ALL buildings.
 * Available from:
 *   - Building Management page (top toolbar)
 *   - Inside MeetingsTab (per-meeting edit form)
 */
export default function MeetingTemplatesDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ legal_text: '', committee_text: '' });
  const [defaults, setDefaults] = useState({ legal_text: '', committee_text: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/meeting-text-templates`)
      .then(r => {
        setForm({
          legal_text: r.data?.legal_text || '',
          committee_text: r.data?.committee_text || '',
        });
        setDefaults({
          legal_text: r.data?.legal_text_default || '',
          committee_text: r.data?.committee_text_default || '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/meeting-text-templates`, form);
      toast.success('Αποθηκεύτηκε');
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = (field) => {
    setForm(prev => ({ ...prev, [field]: defaults[field] || '' }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-none" data-testid="meeting-templates-dialog">
        <DialogHeader>
          <DialogTitle>Πρότυπα Κειμένων Συνελεύσεων</DialogTitle>
          <p className="text-[11px] text-zinc-500 mt-1">
            Τα παρακάτω κείμενα ισχύουν σε όλες τις πολυκατοικίες — προστίθενται αυτόματα στο PDF κάθε συνέλευσης.
          </p>
        </DialogHeader>
        {loading ? (
          <div className="p-8 text-center text-zinc-400">Φόρτωση…</div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold">Νομικό Κείμενο Απαρτίας</Label>
                <button
                  type="button"
                  onClick={() => resetToDefault('legal_text')}
                  className="text-[11px] text-zinc-500 hover:text-zinc-900"
                  data-testid="reset-legal-template-modal"
                >
                  Επαναφορά σε default
                </button>
              </div>
              <Textarea
                value={form.legal_text}
                onChange={(e) => setForm({ ...form, legal_text: e.target.value })}
                className="rounded-none text-sm"
                rows={5}
                data-testid="legal-template-input-modal"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold">Κείμενο Απόφασης Επιτροπής</Label>
                <button
                  type="button"
                  onClick={() => resetToDefault('committee_text')}
                  className="text-[11px] text-zinc-500 hover:text-zinc-900"
                  data-testid="reset-committee-template-modal"
                >
                  Επαναφορά σε default
                </button>
              </div>
              <Textarea
                value={form.committee_text}
                onChange={(e) => setForm({ ...form, committee_text: e.target.value })}
                className="rounded-none text-sm"
                rows={4}
                data-testid="committee-template-input-modal"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">
                Άκυρο
              </Button>
              <Button type="submit" disabled={saving} className="bg-zinc-950 rounded-none" data-testid="save-meeting-templates-modal">
                {saving ? 'Αποθήκευση…' : 'Αποθήκευση'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
